/**
 * 同期エンジン（SPEC §2.1 / §2.2）。
 *
 * 書き込みは必ず「ローカル反映 → キューに積む → 送信」の順で行う。
 * オンラインでもオフラインでも経路が 1 本なので、
 * 「オンラインだけ通る道」が壊れて気付かない、という事故が起きない。
 *
 * 送信は seq 昇順の直列。品目 POST → 購入 POST のような依存が
 * 逆転しないよう、再送待ちが発生したらそこで打ち切る。
 */
import type { ItemRow, LotRow, PurchaseRow, StateResponse } from '../../shared/types';
import { ApiError, NetworkError, apiFetch } from './api';
import { db, type QueueOp } from './db';

/** SPEC §2.1: 最大5回。超過分は「未送信あり」として手動再送に回す */
const MAX_ATTEMPTS = 5;

const backoffMs = (attempts: number): number => Math.min(60_000, 1_000 * 2 ** (attempts - 1));

/* ------------------------------------------------------------------ */
/* 401 ハンドラ（トークン入力画面へ戻す）                                 */
/* ------------------------------------------------------------------ */

let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/* ------------------------------------------------------------------ */
/* レスポンスのマージ                                                    */
/* ------------------------------------------------------------------ */

type AnyRow = ItemRow | PurchaseRow | LotRow;

/**
 * 影響行の最新状態をキャッシュへ反映する（SPEC §5）。
 * `deleted_at` が入った行はローカルから消す（GET /api/state が返さないため）。
 */
export async function mergeResponse(res: unknown): Promise<void> {
  if (typeof res !== 'object' || res === null) return;
  const r = res as Record<string, unknown>;

  const pick = (one: string, many: string): AnyRow[] => {
    const out: AnyRow[] = [];
    if (r[one]) out.push(r[one] as AnyRow);
    if (Array.isArray(r[many])) out.push(...(r[many] as AnyRow[]));
    return out;
  };

  const items = pick('item', 'items') as ItemRow[];
  const purchases = pick('purchase', 'purchases') as PurchaseRow[];
  const lots = pick('lot', 'lots') as LotRow[];
  if (items.length + purchases.length + lots.length === 0) return;

  await db.transaction('rw', db.items, db.purchases, db.lots, async () => {
    await applyRows(db.items, items);
    await applyRows(db.purchases, purchases);
    await applyRows(db.lots, lots);
  });
}

async function applyRows<T extends AnyRow>(
  table: { bulkPut: (rows: T[]) => Promise<unknown>; bulkDelete: (keys: string[]) => Promise<void> },
  rows: T[],
): Promise<void> {
  const live = rows.filter((r) => r.deleted_at === null);
  const dead = rows.filter((r) => r.deleted_at !== null).map((r) => r.id);
  if (live.length) await table.bulkPut(live);
  if (dead.length) await table.bulkDelete(dead);
}

/* ------------------------------------------------------------------ */
/* キュー                                                               */
/* ------------------------------------------------------------------ */

export type NewQueueOp = Pick<QueueOp, 'method' | 'path' | 'body' | 'label'>;

export async function enqueue(op: NewQueueOp): Promise<void> {
  await db.syncQueue.add({
    ...op,
    attempts: 0,
    nextAttemptAt: 0,
    status: 'pending',
    createdAt: Date.now(),
  } as QueueOp);
}

let running: Promise<void> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

/** キューを送信する。多重起動しても実行中の Promise を共有する。 */
export function flush(): Promise<void> {
  if (!running) {
    running = drain().finally(() => {
      running = null;
    });
  }
  return running;
}

/** 例外を握り潰す fire-and-forget 版（操作ハンドラから呼ぶ用） */
export const flushSoon = (): void => {
  void flush().catch(() => {});
};

async function drain(): Promise<void> {
  for (;;) {
    const ops = await db.syncQueue.orderBy('seq').toArray();
    const op = ops.find((o) => o.status === 'pending');
    if (!op || op.seq === undefined) return;

    const wait = op.nextAttemptAt - Date.now();
    if (wait > 0) {
      scheduleRetry(wait);
      return;
    }

    try {
      const res = await apiFetch<unknown>(op.path, { method: op.method, body: op.body });
      await mergeResponse(res);
      await db.syncQueue.delete(op.seq);
      continue;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // トークンが変わった/失効した。再送しても無駄なのでキューは残したまま止める。
        onUnauthorized();
        return;
      }

      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        // 422/409/404 は再送しても結果が変わらない。手動対応に回して後続へ進む。
        await db.syncQueue.update(op.seq, { status: 'failed', lastError: e.message });
        continue;
      }

      // NetworkError / 5xx: 再送すれば成功しうる
      const attempts = op.attempts + 1;
      const lastError = e instanceof Error ? e.message : String(e);
      if (attempts >= MAX_ATTEMPTS) {
        await db.syncQueue.update(op.seq, { status: 'failed', attempts, lastError });
        continue;
      }
      const delay = backoffMs(attempts);
      await db.syncQueue.update(op.seq, {
        attempts,
        nextAttemptAt: Date.now() + delay,
        lastError,
      });
      scheduleRetry(delay);
      // 順序を保つため、この 1 件が通るまで後続は送らない
      return;
    }
  }
}

function scheduleRetry(delay: number): void {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(() => {
    flushSoon();
  }, Math.max(500, delay));
}

/** 失敗扱いになったものを pending に戻して再送（設定画面の「手動再送」） */
export async function retryFailed(): Promise<void> {
  const failed = await db.syncQueue.where('status').equals('failed').toArray();
  await Promise.all(
    failed.map((op) =>
      db.syncQueue.update(op.seq!, { status: 'pending', attempts: 0, nextAttemptAt: 0 }),
    ),
  );
  await flush();
}

/** どうしても送れない 1 件を捨てる（ローカルとサーバがずれる点は UI で警告する） */
export async function discardOp(seq: number): Promise<void> {
  await db.syncQueue.delete(seq);
}

/* ------------------------------------------------------------------ */
/* サーバからの読み取り                                                  */
/* ------------------------------------------------------------------ */

export const LAST_SYNCED_KEY = 'lastSyncedAt';

/**
 * GET /api/state で全件取り直してキャッシュを置き換える（SPEC §2.1）。
 *
 * 未送信キューが残っている状態で置き換えると楽観的更新が消えるため、
 * 先にキューを流し切り、まだ残るなら取得自体を見送る。
 */
export async function refreshFromServer(): Promise<void> {
  if ((await db.syncQueue.count()) > 0) {
    await flush();
    if ((await db.syncQueue.count()) > 0) {
      throw new NetworkError('未送信の変更が残っているため同期を見送りました');
    }
  }

  const state = await apiFetch<StateResponse>('/api/state');

  await db.transaction('rw', db.items, db.purchases, db.lots, db.meta, async () => {
    await Promise.all([db.items.clear(), db.purchases.clear(), db.lots.clear()]);
    await Promise.all([
      db.items.bulkPut(state.items),
      db.purchases.bulkPut(state.purchases),
      db.lots.bulkPut(state.lots),
    ]);
    await db.meta.put({ key: LAST_SYNCED_KEY, value: Date.now() });
  });
}
